import { type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Banner, Button, Input } from '@servicecompressionllc/sc-design-system';
import type { AssistantPolicy } from '../../lib/types.js';
import { CrumbHeader } from './CrumbHeader.js';
import { GUARDRAILS_PATH } from './shared.js';
import { NEW_DOCUMENT, useAddRuleLogic } from './useGuardrailsLogic.js';

const Field = ({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: ReactNode;
}) => (
  <div>
    <label htmlFor={id} className="text-ink mb-1.5 block text-xs font-semibold">
      {label}
    </label>
    {children}
    {hint && <p className="text-ink-subtle mt-1.5 text-xs leading-snug">{hint}</p>}
  </div>
);

/**
 * The default creation path — a batch workflow: each save appends the rule's
 * markdown to the chosen document and clears the form for the next one, with
 * the session's saved rules listed above. Nothing here is stored as fields;
 * submit writes a markdown section into the document (see useAddRuleLogic).
 */
export const AddRuleView = ({
  tenantId,
  policies,
  initialTargetId,
  onDone,
  onCancel,
}: {
  tenantId: string;
  policies: AssistantPolicy[];
  /** Preselect a target document (e.g. chaining from a saved upload). */
  initialTargetId?: string;
  onDone: () => void;
  onCancel: () => void;
}) => {
  const logic = useAddRuleLogic(tenantId, policies, initialTargetId);
  const isNew = logic.targetId === NEW_DOCUMENT;
  const targetName = policies.find((p) => p.id === logic.targetId)?.name ?? logic.newName.trim();
  const fieldsEmpty = Object.values(logic.fields).every((v) => v.trim() === '');
  // "Save" finishes the session: it saves a completed rule first, and with an
  // empty form (everything already saved via "Add another") it just leaves.
  const canFinish = logic.canSubmit || (fieldsEmpty && logic.added.length > 0);

  // Form submit (incl. Enter) = "Add another": save and clear for the next rule.
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!logic.canSubmit) return;
    try {
      await logic.submit();
    } catch {
      // Surfaced via logic.error.
    }
  };

  const handleSave = async () => {
    if (!canFinish || logic.isSubmitting) return;
    try {
      if (logic.canSubmit) await logic.submit();
      onDone();
    } catch {
      // Surfaced via logic.error.
    }
  };

  return (
    <>
      <CrumbHeader title="Add rules" onBack={logic.added.length > 0 ? onDone : onCancel} />
      <div className="mx-auto max-w-[780px]">
        <h2 className="text-ink mb-1.5 text-2xl font-bold">{isNew ? 'New rule' : 'Add rules'}</h2>
        <p className="text-ink-muted mb-5 text-sm">
          One constraint at a time: the situation, what the assistant must never instruct, and what
          to do instead. Each save appends the rule and clears the form for the next.
        </p>

        {!isNew && targetName && logic.added.length === 0 && (
          <p className="text-ink-muted mb-4 text-sm">
            Adding to{' '}
            <Link
              to={`${GUARDRAILS_PATH}/${encodeURIComponent(logic.targetId)}`}
              className="text-link underline underline-offset-2"
            >
              {targetName}
            </Link>
          </p>
        )}

        {logic.added.length > 0 && (
          <div className="border-outline bg-surface-elevated mb-4 rounded-xl border px-4 py-3">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <p className="text-ink text-sm font-semibold">Added this session</p>
              <Badge variant="success">
                {logic.added.length} {logic.added.length === 1 ? 'RULE' : 'RULES'}
              </Badge>
              {targetName && !isNew && (
                <Link
                  to={`${GUARDRAILS_PATH}/${encodeURIComponent(logic.targetId)}`}
                  className="text-link ml-auto text-xs underline underline-offset-2"
                >
                  → {targetName}
                </Link>
              )}
            </div>
            <ul>
              {logic.added.map((rule, i) => (
                <li
                  key={i}
                  className="border-outline flex items-baseline gap-2 border-t py-1.5 text-sm first:border-t-0"
                >
                  <span className="text-success flex-none font-bold" aria-hidden>
                    ✓
                  </span>
                  <span className="text-ink flex-none font-semibold">{rule.situation}</span>
                  <span className="text-ink-subtle truncate text-xs">
                    never instruct {rule.never}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {logic.error && (
          <Banner variant="danger" className="mb-4">
            <Banner.Body>
              <Banner.Title>{logic.error.message}</Banner.Title>
            </Banner.Body>
          </Banner>
        )}

        <form
          onSubmit={handleSubmit}
          className="border-outline bg-surface-elevated space-y-4 rounded-xl border p-5 shadow-sm"
        >
          {isNew && (
            <Field id="rule-new-name" label="Name">
              <Input
                id="rule-new-name"
                type="text"
                value={logic.newName}
                onChange={(e) => logic.setNewName(e.target.value)}
                disabled={logic.isSubmitting}
                placeholder="e.g., Field safety"
                autoComplete="off"
              />
            </Field>
          )}

          <Field
            id="rule-when"
            label="When helping with…"
            hint="The situation where this rule applies — a kind of question or task, in plain words."
          >
            <Input
              id="rule-when"
              type="text"
              value={logic.fields.whenHelpingWith}
              onChange={(e) => logic.setField('whenHelpingWith', e.target.value)}
              disabled={logic.isSubmitting}
              placeholder="clearing a jam or turning equipment by hand"
              autoComplete="off"
            />
          </Field>

          <Field id="rule-never" label="The assistant must never instruct…">
            <Input
              id="rule-never"
              type="text"
              value={logic.fields.neverInstruct}
              onChange={(e) => logic.setField('neverInstruct', e.target.value)}
              disabled={logic.isSubmitting}
              placeholder="reaching near any assembly that could start or turn"
              autoComplete="off"
            />
          </Field>

          <Field id="rule-instead" label="It must instead…">
            <Input
              id="rule-instead"
              type="text"
              value={logic.fields.instead}
              onChange={(e) => logic.setField('instead', e.target.value)}
              disabled={logic.isSubmitting}
              placeholder="confirm lockout before reaching in"
              autoComplete="off"
            />
          </Field>

          <Field id="rule-authority" label="Governing policy (optional)">
            <Input
              id="rule-authority"
              type="text"
              value={logic.fields.authority}
              onChange={(e) => logic.setField('authority', e.target.value)}
              disabled={logic.isSubmitting}
              placeholder="SOP1006"
              autoComplete="off"
            />
          </Field>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              onClick={() => void handleSave()}
              disabled={!canFinish || logic.isSubmitting}
            >
              Save
            </Button>
            <Button type="submit" disabled={!logic.canSubmit}>
              {logic.isSubmitting ? 'Saving…' : 'Add another'}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};
